import { useState } from "react"
import { Plus, Pencil, Trash2, FileText, Clock } from "lucide-react"
import {
  useTaskTemplates,
  useCreateTaskTemplate,
  useUpdateTaskTemplate,
  useDeleteTaskTemplate,
} from "@/hooks/use-task-templates"
import { useTaskCategories } from "@/hooks/use-task-categories"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog"
import { toast } from "sonner"
import type { TaskTemplate, TaskCategory } from "@/lib/types"

// ── Priority labels ────────────────────────────────────────────

const PRIORITY_LABELS: Record<string, string> = {
  low: "Niski",
  normal: "Normalny",
  high: "Wysoki",
  urgent: "Pilny",
}

const PRIORITY_COLORS: Record<string, string> = {
  low: "#6B7280",
  normal: "var(--primary)",
  high: "#F59E0B",
  urgent: "#EF4444",
}

// ── Template Form Dialog ───────────────────────────────────────

interface TemplateFormData {
  name: string
  description: string
  task_type: string
  priority: string
  task_category_id: string
  estimated_hours: string
  is_active: boolean
}

function TemplateFormDialog({
  open,
  onOpenChange,
  template,
  categories,
  onSave,
  isPending,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  template: TemplateFormData | null
  categories: TaskCategory[]
  onSave: (data: TemplateFormData) => void
  isPending: boolean
}) {
  const [name, setName] = useState(template?.name ?? "")
  const [description, setDescription] = useState(template?.description ?? "")
  const [taskType, setTaskType] = useState(template?.task_type ?? "task")
  const [priority, setPriority] = useState(template?.priority ?? "normal")
  const [categoryId, setCategoryId] = useState(template?.task_category_id ?? "")
  const [estimatedHours, setEstimatedHours] = useState(template?.estimated_hours ?? "")
  const [isActive, setIsActive] = useState(template?.is_active ?? true)

  const inputClass = "h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm text-foreground outline-none"

  const handleSave = () => {
    if (!name.trim()) return
    onSave({
      name: name.trim(),
      description: description.trim(),
      task_type: taskType,
      priority,
      task_category_id: categoryId,
      estimated_hours: estimatedHours,
      is_active: isActive,
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{template ? "Edytuj szablon" : "Nowy szablon zadania"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 max-h-[60vh] overflow-y-auto">
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Nazwa *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className={inputClass}
              placeholder="np. Onboarding klienta"
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <label className="text-sm font-medium text-muted-foreground">Opis</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm text-foreground outline-none"
              placeholder="Opis szablonu..."
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Typ zadania</label>
              <select value={taskType} onChange={(e) => setTaskType(e.target.value)} className={inputClass}>
                <option value="task">Zadanie</option>
                <option value="call">Telefon</option>
                <option value="meeting">Spotkanie</option>
                <option value="email">Email</option>
                <option value="followup">Follow-up</option>
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Priorytet</label>
              <select value={priority} onChange={(e) => setPriority(e.target.value)} className={inputClass}>
                <option value="low">Niski</option>
                <option value="normal">Normalny</option>
                <option value="high">Wysoki</option>
                <option value="urgent">Pilny</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Kategoria</label>
              <select value={categoryId} onChange={(e) => setCategoryId(e.target.value)} className={inputClass}>
                <option value="">Brak</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-muted-foreground">Szac. godziny</label>
              <input
                type="number"
                min={0}
                step={0.5}
                value={estimatedHours}
                onChange={(e) => setEstimatedHours(e.target.value)}
                className={inputClass}
                placeholder="np. 2.5"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-muted-foreground">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded"
            />
            Aktywny
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Anuluj
          </Button>
          <Button onClick={handleSave} disabled={!name.trim() || isPending}>
            {isPending ? "Zapisywanie..." : "Zapisz"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Template Row ───────────────────────────────────────────────

function TemplateRow({
  template,
  onEdit,
  onDelete,
}: {
  template: TaskTemplate
  onEdit: () => void
  onDelete: () => void
}) {
  return (
    <div className="flex items-center gap-3 rounded-lg border px-4 py-3 transition-colors hover:bg-muted/20">
      <FileText size={16} className="text-muted-foreground shrink-0" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-foreground">{template.name}</span>
          {template.task_category && (
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium"
              style={{
                backgroundColor: template.task_category.color + "18",
                color: template.task_category.color,
              }}
            >
              <span
                className="h-2 w-2 rounded-full"
                style={{ backgroundColor: template.task_category.color }}
              />
              {template.task_category.name}
            </span>
          )}
          {!template.is_active && (
            <Badge variant="secondary" className="text-[10px]">Nieaktywny</Badge>
          )}
        </div>
        {template.description && (
          <p className="text-xs text-muted-foreground mt-0.5 truncate">{template.description}</p>
        )}
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <Badge
          variant="outline"
          className="text-[11px]"
          style={{ borderColor: PRIORITY_COLORS[template.priority], color: PRIORITY_COLORS[template.priority] }}
        >
          {PRIORITY_LABELS[template.priority] ?? template.priority}
        </Badge>
        {template.estimated_hours != null && template.estimated_hours > 0 && (
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Clock size={12} />
            {template.estimated_hours}h
          </span>
        )}
        {template.template_items_count > 0 && (
          <span className="text-xs text-muted-foreground">
            {template.template_items_count} elem.
          </span>
        )}
        <div className="flex items-center gap-1">
          <button
            onClick={onEdit}
            className="rounded p-1.5 transition-colors hover:bg-muted text-muted-foreground"
          >
            <Pencil size={14} />
          </button>
          <button
            onClick={onDelete}
            className="rounded p-1.5 transition-colors hover:bg-black/5 text-destructive"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main Page ──────────────────────────────────────────────────

export function TaskTemplatesPage() {
  const { data, isLoading } = useTaskTemplates()
  const { data: categoriesData } = useTaskCategories()
  const createMutation = useCreateTaskTemplate()
  const updateMutation = useUpdateTaskTemplate()
  const deleteMutation = useDeleteTaskTemplate()

  const [showAdd, setShowAdd] = useState(false)
  const [editing, setEditing] = useState<TaskTemplate | null>(null)

  const templates: TaskTemplate[] = data?.data ?? []
  const categories: TaskCategory[] = categoriesData?.data ?? []

  const handleCreate = async (formData: TemplateFormData) => {
    try {
      const payload: Record<string, unknown> = {
        name: formData.name,
        description: formData.description || null,
        task_type: formData.task_type,
        priority: formData.priority,
        task_category_id: formData.task_category_id || null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        is_active: formData.is_active,
      }
      await createMutation.mutateAsync(payload)
      toast.success("Szablon utworzony")
      setShowAdd(false)
    } catch {
      toast.error("Nie udalo sie utworzyc szablonu")
    }
  }

  const handleUpdate = async (formData: TemplateFormData) => {
    if (!editing) return
    try {
      const payload: Record<string, unknown> = {
        name: formData.name,
        description: formData.description || null,
        task_type: formData.task_type,
        priority: formData.priority,
        task_category_id: formData.task_category_id || null,
        estimated_hours: formData.estimated_hours ? parseFloat(formData.estimated_hours) : null,
        is_active: formData.is_active,
      }
      await updateMutation.mutateAsync({ id: editing.id, data: payload })
      toast.success("Szablon zaktualizowany")
      setEditing(null)
    } catch {
      toast.error("Nie udalo sie zaktualizowac szablonu")
    }
  }

  const handleDelete = async (template: TaskTemplate) => {
    if (!confirm(`Czy na pewno chcesz usunac szablon "${template.name}"?`)) return
    try {
      await deleteMutation.mutateAsync(template.id)
      toast.success("Szablon usuniety")
    } catch {
      toast.error("Nie udalo sie usunac szablonu")
    }
  }

  if (isLoading) {
    return (
      <div className="mx-auto max-w-[800px] space-y-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-[800px] space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileText size={22} className="text-primary" />
          <div>
            <h1 className="page-title">Szablony zadan</h1>
            <p className="text-sm text-muted-foreground">
              Tworzenie zadan z predefiniowanych szablonow
            </p>
          </div>
        </div>
        <Button onClick={() => setShowAdd(true)}>
          <Plus size={16} /> Nowy szablon
        </Button>
      </div>

      {templates.length === 0 ? (
        <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-input py-12 text-muted-foreground">
          <FileText size={40} />
          <p className="text-sm">Nie masz jeszcze zadnych szablonow zadan</p>
          <Button variant="outline" onClick={() => setShowAdd(true)}>
            <Plus size={16} /> Utworz pierwszy szablon
          </Button>
        </div>
      ) : (
        <div className="space-y-2">
          {templates.map((template) => (
            <TemplateRow
              key={template.id}
              template={template}
              onEdit={() => setEditing(template)}
              onDelete={() => handleDelete(template)}
            />
          ))}
        </div>
      )}

      {/* Add Dialog */}
      <TemplateFormDialog
        open={showAdd}
        onOpenChange={setShowAdd}
        template={null}
        categories={categories}
        onSave={handleCreate}
        isPending={createMutation.isPending}
      />

      {/* Edit Dialog */}
      {editing && (
        <TemplateFormDialog
          open={true}
          onOpenChange={() => setEditing(null)}
          template={{
            name: editing.name,
            description: editing.description || "",
            task_type: editing.task_type,
            priority: editing.priority,
            task_category_id: editing.task_category_id || "",
            estimated_hours: editing.estimated_hours ? String(editing.estimated_hours) : "",
            is_active: editing.is_active,
          }}
          categories={categories}
          onSave={handleUpdate}
          isPending={updateMutation.isPending}
        />
      )}
    </div>
  )
}
